import React, { useState, useEffect, useRef } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ContentContainer, MainContent, CenteredCard, StyledTableContainer, StyledTableHead, StyledTableColumn, StyledTableCell, StyledChartCard, RefreshIcon, CustomTextField, HeaderTitle } from '../Styles/Vacuum_Level';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextField, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Grid from '@mui/material/Grid';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import refreshIcon from '../Images/refresh-icon.svg';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Parts/Sidebar';
import Header from '../Parts/Header';
import Footer from '../Parts/Footer';
import { useLogoTitle } from '../classes/LogoTitleContext';
import axios from 'axios';
import getAuthHeaders from '../Auth/Authentication';
import { API_ENDPOINTS } from '../Auth/apiConfig';
import pdfIcon from '../Images/pdf-icon.svg';
import excelIcon from '../Images/excel-icon.svg';
import { DatePicker } from '@mui/x-date-pickers/DatePicker'; // Import DatePicker explicitly
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer, Brush } from 'recharts';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { utils, writeFile } from 'xlsx';
import { Divider } from '@mui/material';

const PotAnalytics = ({ onLogout, loggedInUser }) => {
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    const navigate = useNavigate();
    const { logoUrl, projectTitle } = useLogoTitle();
    const [loading, setLoading] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [tableData, setTableData] = useState([]);
    const [selectedDateFrom, setSelectedDateFrom] = useState(null);
    const [selectedDateTo, setSelectedDateTo] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [minValue, setMinValue] = useState(null);
    const [maxValue, setMaxValue] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [tankTableData, setTankTableData] = useState([]);
    const [filteredTankData, setFilteredTankData] = useState([]);
    const [tankChartData, setTankChartData] = useState([]);
    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());

    const today = dayjs();
    const thirtyDaysAgo = today.subtract(30, 'day');

    useEffect(() => {
        fetchData();
    }, []);

    const chartRef = useRef(null);
    const [scrollPosition, setScrollPosition] = useState(0);
    const totalWidth = chartData.length * 10; // Dynamic width based on data size

    const handleScroll = (e) => {
        setScrollPosition(e.target.scrollLeft);
    };

    const fetchData = async () => {
        if (!fromDate || !toDate) {
            console.warn("Please select both From and To dates.");
            return;
        }

        let selectedFromDate = fromDate || new Date();  // Default to today
        let selectedToDate = toDate || new Date();      // Default to today

        const params = {
            IntTimeDiff: loggedInUser.TimeDiff,
            clientId: loggedInUser.clientId,
            fromDate: selectedFromDate.toISOString().split('T')[0], // YYYY-MM-DD
            toDate: selectedToDate.toISOString().split('T')[0]     // YYYY-MM-DD
        };

        try {
            setLoading(true);
            const authHeaders = getAuthHeaders();
            const response = await axios.get(`${API_ENDPOINTS.dailyvacuumlevel}`, {
                params: params,
                headers: authHeaders,
            });

            const { dailyVacuumData, dailyTankData, adjustedTimeData } = response.data;

            // Update table and filtered data
            setTableData(dailyVacuumData);
            setFilteredData(dailyVacuumData);

            // Prepare chart data
            const adjustedTimeChartData = adjustedTimeData.map((item) => ({
                timeoftwominutes: item.time,
                TwoMinutesVacuumAverage: parseFloat(item.DailyVacuumAverage),
            }));

            const adjustedTankChartData = adjustedTimeData.map((item) => ({
                timeoftwominutestank: item.time,
                TwoMinutesTankAverage: parseFloat(item.DailyTankAverage),
            }));

            setChartData(adjustedTimeChartData);
            setTankChartData(adjustedTankChartData);

            // Find min and max values
            const adjustedTimeValues = adjustedTimeChartData.map((item) => item.TwoMinutesVacuumAverage);
            const adjustedTankValues = adjustedTankChartData.map((item) => item.TwoMinutesTankAverage);

            setMinValue({
                adjustedTime: Math.min(...adjustedTimeValues),
                adjustedTank: Math.min(...adjustedTankValues),
            });

            setMaxValue({
                adjustedTime: Math.max(...adjustedTimeValues),
                adjustedTank: Math.max(...adjustedTankValues),
            });

        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        fetchData();  // Fetch data based on selected date range
    };

    const handleDownloadPDF = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
        });

        // Function to generate table content
        const generateTableContent = (tableData, columns, startY) => {
            const rows = tableData.map(row => {
                return columns.map(column => {
                    switch (column.key) {
                        case 'Date':
                            return row.Date || '0';
                        case 'DailyVacuumAverage':
                            return row.DailyVacuumAverage || '0';
                        default:
                            return '';
                    }
                });
            });

            // Add a smaller space after each table
            doc.autoTable({
                head: [columns.map(column => column.label)],
                body: rows,
                startY: startY,
                headStyles: {
                    fillColor: [200, 200, 200], // Grey color (RGB: 169, 169, 169)
                    textColor: [0, 0, 0], // Text color (black)
                    fontSize: 12, // Font size for header text
                    fontStyle: 'bold', // Font style for header text
                },
                theme: 'striped', // Optional: Adds alternating row colors
            });

            return doc.autoTable.previous.finalY + 10; // Adjust this value as needed
        };

        // Add Title
        const title = 'Vacuum Level Report';
        const titleWidth = doc.getStringUnitWidth(title) * doc.internal.getFontSize() / doc.internal.scaleFactor;
        const pageWidth = doc.internal.pageSize.width;
        const xTitle = (pageWidth - titleWidth) / 2;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.text(title, xTitle, 20);

        // Add Project Name below the title
        const projectName = `Project Name: ${loggedInUser && loggedInUser.project_description}`;
        const projectNameWidth = doc.getStringUnitWidth(projectName) * doc.internal.getFontSize() / doc.internal.scaleFactor;
        const xProjectName = (pageWidth - projectNameWidth) / 2;
        doc.text(projectName, xProjectName, 30);

        // Add From Date below the Project Name
        const fromDateText = `Date between: ${selectedDateFrom ? selectedDateFrom.format('DD-MM-YYYY') : 'Select From date'} / ${selectedDateTo ? selectedDateTo.format('DD-MM-YYYY') : 'Select To Date'}`;
        const fromDateWidth = doc.getStringUnitWidth(fromDateText) * doc.internal.getFontSize() / doc.internal.scaleFactor;
        const xFromDate = (pageWidth - fromDateWidth) / 2;
        doc.text(fromDateText, xFromDate, 40);

        // Starting y-position for the first table
        let startY = 50;

        // Generate PDF content based on each table
        if (tableData.length > 0) {
            const columns1 = [
                { key: 'Date', label: 'Date' },
                { key: 'DailyVacuumAverage', label: 'Vacuum Level' },
            ];
            startY = generateTableContent(tableData, columns1, startY);
        }

        // Save the PDF
        doc.save('Vacuum_Level_report.pdf');
    };

    const handleDownloadExcel = () => {
        const XLSX = require('xlsx');
        const wb = XLSX.utils.book_new();
        const wsData = [];

        // Header lines
        const headerLines = [
            ['Vacuum Level Report'],
            [`Project Name: ${loggedInUser && loggedInUser.project_description}`],
            [`Date between: ${selectedDateFrom ? selectedDateFrom.format('DD-MM-YYYY') : 'Select From Date'} / ${selectedDateTo ? selectedDateTo.format('DD-MM-YYYY') : 'Select To Date'}`]
        ];
        wsData.push(...headerLines);

        // Function to generate Excel content for a specific data set
        const generateExcelContent = (data, columns) => {
            const header = columns.map(column => column.label);
            const rows = data.map(row => columns.map(column => {
                switch (column.key) {
                    case 'Date':
                        return row.Date || '0';
                    case 'DailyVacuumAverage':
                        return row.DailyVacuumAverage || '0';
                    default:
                        return '';
                }
            }));

            // Add empty row for spacing
            rows.push(Array(header.length).fill(''));

            return [header, ...rows];
        };

        // Generate PDF content based on each table
        if (tableData.length > 0) {
            const columns1 = [
                { key: 'Date', label: 'Date' },
                { key: 'DailyVacuumAverage', label: 'Vacuum Level' },
            ];
            wsData.push(...generateExcelContent(tableData, columns1));

        }

        // Create a single worksheet from all data
        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Set column widths and style for centered alignment
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const colWidth = wsData.reduce((acc, row) => Math.max(acc, `${row[C]}`.length), 0) + 3;
            ws['!cols'] = ws['!cols'] || [];
            ws['!cols'][C] = { wch: colWidth, s: { alignment: { horizontal: 'center' } } };
        }

        // Append the worksheet to the workbook
        XLSX.utils.book_append_sheet(wb, ws, 'VacLvl');

        // Save the Excel file
        XLSX.writeFile(wb, 'Vacuum_Level_report.xlsx');
    };

    const handleDateFrom = (date) => {
        setSelectedDateFrom(date);
    };

    const handleDateTo = (date) => {
        setSelectedDateTo(date);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(event.target.value);
    };

    const CustomTooltipForVacuumLevelChart = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    backgroundColor: '#ffffff', // Pure white background
                    color: '#333', // Dark text for contrast
                    padding: '14px 18px',
                    borderRadius: '10px', // Smooth corners
                    border: '2px solid #1E88E5', // Blue border for a sharp look
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)', // Soft shadow for depth
                    fontSize: '14px',
                    minWidth: '180px', // Ensures consistent width
                    fontFamily: '"Poppins", sans-serif', // Modern typography
                    transition: 'all 0.2s ease-in-out' // Smooth hover effect
                }}>
                    <p style={{
                        margin: '0 0 6px',
                        fontWeight: 'bold',
                        color: '#1E88E5',
                        fontSize: '16px',
                        borderBottom: '1px solid #ddd', // Subtle divider
                        paddingBottom: '4px'
                    }}>
                        🕒 {label} {/* Time label with an icon */}
                    </p>
                    <p style={{
                        margin: 0,
                        fontWeight: '600',
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        📊 Vacuum Level:
                        <span style={{
                            fontWeight: 'bold',
                            color: '#1E88E5', // Red for alert effect
                            marginLeft: '6px'
                        }}>
                            {payload[0].value.toFixed(2)}
                        </span>
                    </p>
                </div>
            );
        }
        return null;
    };

    const CustomTooltipForTankLevelChart = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    backgroundColor: '#ffffff', // Pure white background
                    color: '#333', // Dark text for contrast
                    padding: '14px 18px',
                    borderRadius: '10px', // Smooth corners
                    border: '2px solid #4CAF50', // Blue border for a sharp look
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)', // Soft shadow for depth
                    fontSize: '14px',
                    minWidth: '180px', // Ensures consistent width
                    fontFamily: '"Poppins", sans-serif', // Modern typography
                    transition: 'all 0.2s ease-in-out' // Smooth hover effect
                }}>
                    <p style={{
                        margin: '0 0 6px',
                        fontWeight: 'bold',
                        color: '#4CAF50',
                        fontSize: '16px',
                        borderBottom: '1px solid #ddd', // Subtle divider
                        paddingBottom: '4px'
                    }}>
                        🕒 {label} {/* Time label with an icon */}
                    </p>
                    <p style={{
                        margin: 0,
                        fontWeight: '600',
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        📊 Tank Level:
                        <span style={{
                            fontWeight: 'bold',
                            color: '#4CAF50', // Red for alert effect
                            marginLeft: '6px'
                        }}>
                            {payload[0].value.toFixed(2)}
                        </span>
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div style={{ backgroundColor: '#EBEFF3' }}>
                <Sidebar loggedInUser={loggedInUser} />
                <Header logoUrl={logoUrl} loggedInUser={loggedInUser} onLogout={onLogout} />
                <ContentContainer>
                    <MainContent>
                        <Grid container spacing={2} justifyContent="center">
                            <Grid item xs={12} lg={20}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <HeaderTitle>
                                            Vacuum & Tank Level{" "}
                                            {loggedInUser.clientId == 505 && "(Grey Water)"}
                                        </HeaderTitle>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <a href="#" onClick={handleDownloadPDF} style={{ marginRight: '5px' }}>
                                                <img src={pdfIcon} alt="PDF" style={{ height: '50px' }} />
                                            </a>

                                            <a href="#" onClick={handleDownloadExcel} style={{ marginRight: '10px' }}>
                                                <img src={excelIcon} alt="Excel" style={{ height: '50px' }} />
                                            </a>
                                            <RefreshIcon
                                                title="Refresh"
                                                src={refreshIcon}
                                                label="Click to refresh"
                                                alt="Refresh"
                                                onClick={() => window.location.reload()}
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </Box>
                                    </Box>
                                    {/*
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormGroup>
                                                    <FormControl variant="standard" sx={{ width: '100%' }}>
                                                        <DatePicker
                                                            label="Select From"
                                                            value={selectedDateFrom}
                                                            onChange={handleDateFrom}
                                                            minDate={thirtyDaysAgo}
                                                            maxDate={today}
                                                            renderInput={(params) => <CustomTextField {...params} />}
                                                            sx={{
                                                                width: '100%',
                                                                '& .MuiInputLabel-root': {
                                                                    fontSize: '0.75rem',
                                                                },
                                                                '& .MuiInputBase-root': {
                                                                    fontSize: '0.8rem',
                                                                },
                                                            }}
                                                        />
                                                    </FormControl>
                                                </FormGroup>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormGroup>
                                                    <FormControl variant="standard" sx={{ width: '100%' }}>
                                                        <DatePicker
                                                            label="Select To"
                                                            value={selectedDateTo}
                                                            onChange={handleDateTo}
                                                            minDate={selectedDateFrom || thirtyDaysAgo}
                                                            maxDate={today}
                                                            renderInput={(params) => <CustomTextField {...params} />}
                                                            sx={{
                                                                width: '100%',
                                                                '& .MuiInputLabel-root': {
                                                                    fontSize: '0.75rem',
                                                                },
                                                                '& .MuiInputBase-root': {
                                                                    fontSize: '0.8rem',
                                                                },
                                                            }}
                                                        />
                                                    </FormControl>
                                                </FormGroup>
                                            </Grid>
                                        </Grid>
                                        */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'center',
                                            marginTop: '10px',
                                        }}
                                    >
                                        {/*
                                            <Box sx={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
                                                <FormControl variant="standard">
                                                    <InputLabel id="row-selection-label">Entries</InputLabel>
                                                    <Select
                                                        labelId="row-selection-label"
                                                        id="row-selection"
                                                        value={rowsPerPage}
                                                        onChange={handleChangeRowsPerPage}
                                                        label="Rows"
                                                    >
                                                        <MenuItem value={10}>10</MenuItem>
                                                        <MenuItem value={20}>20</MenuItem>
                                                        <MenuItem value={30}>30</MenuItem>
                                                        <MenuItem value={40}>40</MenuItem>
                                                        <MenuItem value={50}>50</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                                */}
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', }}>
                                            {/*
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={handleSearch}
                                                    disabled={loading}
                                                    sx={{
                                                        marginRight: '10px',
                                                        borderRadius: '5px',
                                                        padding: '2px 6px',
                                                        fontSize: '0.75rem',
                                                        '& .MuiButton-startIcon': {
                                                            margin: 0,
                                                        },
                                                        '&:hover': {
                                                            backgroundColor: '#D9FEE5',
                                                        },
                                                    }}
                                                >
                                                    {loading ? <CircularProgress size={20} /> : 'Search'}
                                                </Button>

                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={handleClear}
                                                    sx={{
                                                        marginRight: '50px',
                                                        borderRadius: '5px',
                                                        padding: '2px 6px',
                                                        fontSize: '0.75rem',
                                                        '& .MuiButton-startIcon': {
                                                            margin: 0,
                                                        },
                                                        '&:hover': {
                                                            backgroundColor: '#FFCCCB',
                                                        },
                                                    }}
                                                >
                                                    Clear
                                                </Button>
                                                */}

                                        </Box>
                                    </Box>
                                </CardContent>

                            </Grid>
                        </Grid>

                        {/* Chart Section */}
                        <Grid container spacing={2} justifyContent="center">
                            <Grid item xs={12} lg={10}>
                                <StyledChartCard>
                                    {/* Date Range Picker in Top Right */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {(() => {
                                                    const today = new Date();
                                                    const lastSixtyDays = new Date();
                                                    lastSixtyDays.setDate(today.getDate() - 60);

                                                    return (
                                                        <>
                                                            {/* From Date Picker */}
                                                            <DatePicker
                                                                label="From"
                                                                value={fromDate}
                                                                onChange={(date) => setFromDate(date)}
                                                                minDate={lastSixtyDays} // Minimum date is 60 days ago
                                                                maxDate={today} // Maximum date is today
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        size="small"
                                                                        sx={{
                                                                            width: 95,
                                                                            fontSize: '0.75rem',
                                                                            backgroundColor: '#f5f5f5',
                                                                            borderRadius: 1.5,
                                                                            '& .MuiOutlinedInput-root': {
                                                                                '& fieldset': { borderColor: '#ccc' },
                                                                                '&:hover fieldset': { borderColor: '#999' },
                                                                                '&.Mui-focused fieldset': { borderColor: '#007BFF' }
                                                                            }
                                                                        }}
                                                                    />
                                                                )}
                                                            />

                                                            {/* To Date Picker */}
                                                            <DatePicker
                                                                label="To"
                                                                value={toDate}
                                                                onChange={(date) => setToDate(date)}
                                                                minDate={lastSixtyDays} // Minimum date is 60 days ago
                                                                maxDate={today} // Maximum date is today
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        size="small"
                                                                        sx={{
                                                                            width: 95,
                                                                            fontSize: '0.75rem',
                                                                            backgroundColor: '#f5f5f5',
                                                                            borderRadius: 1.5,
                                                                            '& .MuiOutlinedInput-root': {
                                                                                '& fieldset': { borderColor: '#ccc' },
                                                                                '&:hover fieldset': { borderColor: '#999' },
                                                                                '&.Mui-focused fieldset': { borderColor: '#007BFF' }
                                                                            }
                                                                        }}
                                                                    />
                                                                )}
                                                            />
                                                        </>
                                                    );
                                                })()}
                                                {/* Search Button */}
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={handleSearch}
                                                    disabled={loading}
                                                    sx={{
                                                        borderRadius: 1.5,
                                                        padding: '3px 8px',
                                                        fontSize: '0.75rem',
                                                        minWidth: 70,
                                                        '&:hover': {
                                                            backgroundColor: '#D9FEE5',
                                                        },
                                                    }}
                                                >
                                                    {loading ? <CircularProgress size={16} /> : 'Search'}
                                                </Button>
                                            </Box>
                                        </LocalizationProvider>
                                    </Box>
                                    <CardContent>
                                        {/* Vacuum Level Chart */}
                                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'dodgerblue' }}>
                                                Vacuum Level Chart
                                            </Typography>
                                            <Divider sx={{ my: 1, backgroundColor: 'dodgerblue', height: 3, width: '10%', mx: 'auto' }} />
                                        </Box>

                                        <ResponsiveContainer width="100%" height={400}>
                                            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 60 }}>
                                                {/* Increased bottom margin from 40 to 60 */}

                                                <XAxis
                                                    dataKey="timeoftwominutes"
                                                    tick={{ fontSize: 11 }}
                                                    interval={Math.floor(chartData.length / 5)}
                                                    label={{
                                                        value: "Time",
                                                        position: "insideBottom",
                                                        offset: -3, // Increased offset to move label below the Brush
                                                        fontSize: 14,
                                                        style: { fill: "black" }
                                                    }}
                                                />

                                                <YAxis
                                                    type="number"
                                                    allowDecimals={true}
                                                    tick={{ fontSize: 12 }}
                                                    tickFormatter={(tick) => tick.toFixed(2)}
                                                    domain={['auto', 'auto']}
                                                    reversed={true}
                                                    label={{
                                                        value: "Vacuum Data values",
                                                        angle: -90,
                                                        position: "insideLeft",
                                                        offset: 15, // Slightly increased for better visibility
                                                        fontSize: 14,
                                                        style: { fill: "black" }
                                                    }}
                                                />

                                                <Tooltip content={<CustomTooltipForVacuumLevelChart />} />

                                                <Legend />

                                                <Line
                                                    type="monotone"
                                                    dataKey="TwoMinutesVacuumAverage"
                                                    stroke="#1E88E5"
                                                    strokeWidth={2}
                                                    fillOpacity={1}
                                                    fill="url(#colorVacuum)"
                                                    dot={{ stroke: '#0056b3', strokeWidth: 2, fill: 'white', r: 2.5 }}
                                                    name="Vacuum Level"
                                                />

                                                <CartesianGrid strokeDasharray="3 3" stroke="grey" opacity={0.3} />

                                                {/* Brush for Zooming */}
                                                <Brush
                                                    dataKey="timeoftwominutes"
                                                    height={20}
                                                    stroke="#1E88E5"
                                                    fill="rgba(30, 136, 229, 0.2)"
                                                    travellerWidth={12}
                                                    y={301} // Moves brush higher so the label remains visible
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>


                                        <Divider sx={{ my: 3, backgroundColor: '#d0d0d0' }} />

                                        {/* Tank Level Chart */}
                                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                                                Tank Level Chart
                                            </Typography>
                                            <Divider sx={{ my: 1, backgroundColor: '#4CAF50', height: 3, width: '10%', mx: 'auto' }} />
                                        </Box>

                                        <ResponsiveContainer width="100%" height={400}>
                                            <LineChart data={tankChartData} margin={{ top: 20, right: 30, left: 10, bottom: 60 }}>
                                                {/* Increased bottom margin from 40 to 60 */}

                                                <defs>
                                                    <linearGradient id="colorTank" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.9} />
                                                        <stop offset="95%" stopColor="#C8E6C9" stopOpacity={0.1} />
                                                    </linearGradient>
                                                </defs>

                                                <XAxis
                                                    dataKey="timeoftwominutestank"
                                                    tick={{ fontSize: 11 }}
                                                    interval={Math.floor(chartData.length / 5)}
                                                    label={{
                                                        value: "Time",
                                                        position: "insideBottom",
                                                        offset: -3, // Increased offset to move label below the Brush
                                                        fontSize: 14,
                                                        style: { fill: "black" }
                                                    }}
                                                />

                                                <YAxis
                                                    type="number"
                                                    allowDecimals={true}
                                                    tick={{ fontSize: 12 }}
                                                    tickFormatter={(tick) => tick.toFixed(2)}
                                                    domain={['auto', 'auto']}
                                                    reversed={false}
                                                    label={{
                                                        value: "Tank Data values",
                                                        angle: -90,
                                                        position: "insideLeft",
                                                        offset: 15, // Slightly increased for better visibility
                                                        fontSize: 14,
                                                        style: { fill: "black" }
                                                    }}
                                                />

                                                <Tooltip content={<CustomTooltipForTankLevelChart />} />

                                                <Legend />

                                                <Line
                                                    type="monotone"
                                                    dataKey="TwoMinutesTankAverage"
                                                    stroke="#2E8B57"
                                                    strokeWidth={2}
                                                    fillOpacity={1}
                                                    fill="url(#colorTank)"
                                                    dot={{ stroke: '#006400', strokeWidth: 2, fill: 'white', r: 2.5 }}
                                                    name="Tank Level"
                                                />

                                                <CartesianGrid strokeDasharray="3 3" stroke="grey" opacity={0.3} />

                                                {/* Brush for Zooming */}
                                                <Brush
                                                    dataKey="timeoftwominutestank"
                                                    height={20}
                                                    stroke="#4CAF50"
                                                    fill="rgba(76, 175, 80, 0.2)"
                                                    travellerWidth={12}
                                                    y={301} // Moves brush higher so the label remains visible
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </CardContent>
                                </StyledChartCard>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} lg={20}>

                            <CardContent>
                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <>
                                        {tableData.length > 0 ? (
                                            <StyledTableContainer>
                                                <Table>
                                                    <StyledTableHead>
                                                        <TableRow>
                                                            <StyledTableColumn>Date</StyledTableColumn>
                                                            <StyledTableColumn>Average vacuum level</StyledTableColumn>
                                                        </TableRow>
                                                    </StyledTableHead>
                                                    <TableBody>
                                                        {(filteredData.length > 0 ? filteredData : tableData).map((row) => (
                                                            <TableRow key={row.id}>
                                                                <StyledTableCell>{row.Date}</StyledTableCell>
                                                                <StyledTableCell>{row.DailyVacuumAverage}</StyledTableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </StyledTableContainer>
                                        ) : (
                                            <Typography variant="body2" color="textSecondary" component="p">
                                                No data available
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Grid>

                    </MainContent>
                </ContentContainer >
                <Footer />
                <ToastContainer />
            </div >
        </LocalizationProvider >
    );
};

export default PotAnalytics;